/**
 * @format
 */
import axios from "axios";
import { uri } from "constants/common";
import { getRecaptchaToken, needsRecaptcha } from "helpers/recaptcha";

/**
 * Attaches a fresh reCAPTCHA v3 token to challenged submissions. Registered on every axios
 * instance so it covers the login/password forms and all interpreter-portal submits
 * without touching individual call sites. No-ops when reCAPTCHA isn't configured.
 */
const attachRecaptcha = (instance) => {
  instance.interceptors.request.use(async (config) => {
    if (needsRecaptcha(config.url, config.method)) {
      const token = await getRecaptchaToken((config.url || "submit").replace(/[^a-zA-Z0-9_]/g, "_"));
      if (token) {
        config.headers["X-Recaptcha-Token"] = token;
      }
    }
    return config;
  });
};

export default class Api {
  _api = null;
  static _token;

  constructor() {
    this._api = axios.create({
      baseURL: uri, // replace with your actual base URL
    });

    // Request Interceptor (For Adding Token in Headers)
    this._api.interceptors.request.use(
      (config) => {
        if (Api._token) {
          config.headers.Authorization = `Bearer ${Api._token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response Interceptor (For Handling 401 Unauthorized)
    this._api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && error.config.url !== "/login") {
          console.error("Unauthorized! Logging out...");
          localStorage.clear();
          setTimeout(() => {
            window.location.href = "/login";
          }, 500);
          return Promise.reject({ message: "Unauthorized access" });
        }
        return Promise.reject(error);
      }
    );

    attachRecaptcha(this._api);
  }

  static init = ({ url }) => {
    try {
      this._api = axios.create({
        baseURL: url,
        timeout: 10000,
      });

      // Add response interceptor after reinitialization
      this._api.interceptors.response.use(
        (response) => response,
        (error) => {
          if (error.response?.status === 401 && error.config.url !== "/login") {
            localStorage.clear();
            setTimeout(() => {
              window.location.href = "/login";
            }, 500);
            return Promise.reject({ message: "Unauthorized access" });
          }
          return Promise.reject(error);
        }
      );

      attachRecaptcha(this._api);
    } catch (error) {
      return error;
    }
  };

  static removeClientToken = async () => {
    this.requestInterceptor = undefined;
    this._api.interceptors.request.clear();
  };

  static setClientToken = async (token) => {
    Api._token = token;
    // this.token = token;
    // this.interceptor = this._api.interceptors.request.use(function (config) {
    //   config.headers.Authorization = `Bearer ${token}`;
    //   this.requestInterceptor = token;
    //   return config;
    // })
  };

  static getTokenFromInterceptor = () => {
    return this.requestInterceptor;
  };

  //#region AUTH APIS
  static login = async (data) => {
    try {
      const response = await this._api.post("/login", data);
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static signup = async (data) => {
    try {
      const response = await this._api.post("/signup", data);
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static forgotPassword = async (data) => {
    try {
      const response = await this._api.post("/forgot-password", data);
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // Checked when the reset page loads, so the form only appears for a good link
  static validateResetToken = async (data) => {
    try {
      const response = await this._api.post("/validate-reset-token", data);
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static resetPassword = async (data) => {
    try {
      const response = await this._api.post("/reset-password", data);
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region PROFILE

  static changePassword = async (data) => {
    try {
      const response = await this._api.post("/changepassword", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region HOSPITAL
  static addHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateHospital = async (data, itemId) => {
    try {
      const response = await this._api.post("/hospital/edit/" + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeHospital = async (itemId) => {
    try {
      const response = await this._api.post("/hospital/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchHospitals = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/hospital/list", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region POOL
  static addPool = async (data) => {
    try {
      const response = await this._api.post("/pool", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updatePool = async (data, itemId) => {
    try {
      const response = await this._api.post("/pool/edit/" + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removePool = async (itemId) => {
    try {
      const response = await this._api.post("/pool/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultiplePools = async (data) => {
    try {
      const response = await this._api.post("/pool/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchPools = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/pool/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region  MEETING PLACE
  static addMeetingPlace = async (data) => {
    try {
      const response = await this._api.post("/meetingplace", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateMeetingPlace = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/meetingplace/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMeetingPlace = async (itemId) => {
    try {
      const response = await this._api.post("/meetingplace/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultiplePlaces = async (data) => {
    try {
      const response = await this._api.post("/meetingplace/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMeetingPlaces = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/meetingplace/list", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region CASE TYPE
  static addCourtroom = async (data) => {
    try {
      const response = await this._api.post("/client/courtrooms", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateCourtroom = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/courtrooms/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeCourtroom = async (itemId) => {
    try {
      const response = await this._api.post("/client/courtrooms/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleCourtrooms = async (data) => {
    try {
      const response = await this._api.post("/client/courtrooms/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchCourtrooms = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/courtrooms/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion


  //#region JURISDICTIONS
  static addJurisdiction = async (data) => {
    try {
      const response = await this._api.post("/client/jurisdictions", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateJurisdiction = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/jurisdictions/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeJurisdiction = async (itemId) => {
    try {
      const response = await this._api.post("/client/jurisdictions/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleJurisdictions = async (data) => {
    try {
      const response = await this._api.post("/client/jurisdictions/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchJurisdictions = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/jurisdictions/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion


  //#region JOB LOCATION
  static addJobLocation = async (data) => {
    try {
      const response = await this._api.post("/client/job-locations", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateJobLocation = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/job-locations/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeJobLocation = async (itemId) => {
    try {
      const response = await this._api.post("/client/job-locations/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleJobLocations = async (data) => {
    try {
      const response = await this._api.post("/client/job-locations/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchJobLocationById = async (id) => {
    try {
      const response = await this._api.get(`/client/job-locations/location/${id}`, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchJobLocationCourtrooms = async (id) => {
    try {
      const response = await this._api.get(`/client/job-locations/jobCourtrooms/${id}`, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchJobLocationJurisdictions = async (id) => {
    try {
      const response = await this._api.get(`/client/job-locations/jobJurisdictions/${id}`, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchJobLocations = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/job-locations/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region PARTY TYPE
  static addPartyType = async (data) => {
    try {
      const response = await this._api.post("/client/party-types", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updatePartyType = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/party-types/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removePartyType = async (itemId) => {
    try {
      const response = await this._api.post("/client/party-types/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultiplePartyTypes = async (data) => {
    try {
      const response = await this._api.post("/client/party-types/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchPartyTypes = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/party-types/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region CASE TYPE
  static addCaseType = async (data) => {
    try {
      const response = await this._api.post("/client/case-types", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateCaseType = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/case-types/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeCaseType = async (itemId) => {
    try {
      const response = await this._api.post("/client/case-types/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleCaseTypes = async (data) => {
    try {
      const response = await this._api.post("/client/case-types/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchCaseTypes = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/case-types/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region HEARING TYPE
  static addHearingType = async (data) => {
    try {
      const response = await this._api.post("/client/hearing-types", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateHearingType = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/hearing-types/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeHearingType = async (itemId) => {
    try {
      const response = await this._api.post("/client/hearing-types/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleHearingTypes = async (data) => {
    try {
      const response = await this._api.post("/client/hearing-types/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchHearingTypes = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/hearing-types/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region CASES
  static addCase = async (data) => {
    try {
      const headers = {
        Authorization: `Bearer ${Api._token}`,
      };
      if (data instanceof FormData) {
        headers["Content-Type"] = "multipart/form-data";
      }
      const response = await this._api.post("/client/appointments/cases", data, {
        headers,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateCase = async (data, itemId) => {
    try {
      const headers = {
        Authorization: `Bearer ${Api._token}`,
      };
      if (data instanceof FormData) {
        headers["Content-Type"] = "multipart/form-data";
      }
      const response = await this._api.post(
        "/client/appointments/cases/edit/" + itemId,
        data,
        {
          headers,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeCase = async (itemId) => {
    try {
      const response = await this._api.post("/client/appointments/cases/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleCases = async (data) => {
    try {
      const response = await this._api.post("/client/appointments/cases/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static moveMultipleCases = async (data) => {
    try {
      const response = await this._api.post("/client/appointments/cases/moves/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchCases = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/appointments/cases/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // King County DCOR case-number lookup — called DIRECTLY from the browser.
  // The KC API host is internal to the KC network (where the internal team's browsers run),
  // so the call originates client-side with HTTP Basic auth. Uses a bare axios (not this._api,
  // which targets our backend and sends a Bearer token).
  static fetchKcCases = async (startswith) => {
    try {
      const response = await axios.get(process.env.REACT_APP_KC_CASES_API_URL, {
        params: { startswith },
        headers: {
          Authorization:
            "Basic " +
            btoa(
              `${process.env.REACT_APP_KC_CASES_API_USERNAME}:${process.env.REACT_APP_KC_CASES_API_PASSWORD}`
            ),
        },
      });
      return response;
    } catch (error) {
      return error.response; // undefined on CORS/network failure → handled as "unavailable"
    }
  };
  //#endregion


  //#region CITY
  static addCity = async (data) => {
    try {
      const response = await this._api.post("/client/city", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateCity = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/city/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeCity = async (itemId) => {
    try {
      const response = await this._api.post("/client/city/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleCitys = async (data) => {
    try {
      const response = await this._api.post("/client/city/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchCitys = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/city/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region STATE
  static addState = async (data) => {
    try {
      const response = await this._api.post("/client/state", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateState = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/state/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeState = async (itemId) => {
    try {
      const response = await this._api.post("/client/state/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleStates = async (data) => {
    try {
      const response = await this._api.post("/client/state/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchStates = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/state/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchStatesInterpreter = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).forEach((key) => {
        if (query[key]) params[key] = query[key];
      });
    }
    try {
      const response = await this._api.get("/interpreter/states/list", {
        headers: { Authorization: `Bearer ${Api._token}` },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region LANGUAGES
  static addLanguage = async (data) => {
    try {
      const response = await this._api.post("/client/language", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateLanguage = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/language/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeLanguage = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/language/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleLanguages = async (data) => {
    try {
      const response = await this._api.post("/client/language/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchLanguages = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/language/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchTiersLanguages = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/language/tier-languages", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region LANGUAGES TIERS
  static addLanguageTiers = async (data) => {
    try {
      const response = await this._api.post("/client/language-tiers", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateLanguageTiers = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/language-tiers/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeLanguageTiers = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/language-tiers/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleLanguagesTiers = async (data) => {
    try {
      const response = await this._api.post(
        "/client/language-tiers/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchLanguagesTiers = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/language-tiers/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTemplateLanguagesTiers = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/language-tiers/template-tiers",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region VENDOR
  static addVendor = async (formData) => {
    try {
      const response = await this._api.post("client/vendor", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateVendor = async (formData, itemId) => {
    try {
      const response = await this._api.post(
        "client/vendor/edit/" + itemId,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeVendor = async (itemId) => {
    try {
      const response = await this._api.post("client/vendor/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleVendors = async (data) => {
    try {
      const response = await this._api.post("client/vendor/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchVendors = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("client/vendor/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchVendorsListPage = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("client/vendor/list-page", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region ADMIN
  static addAdmin = async (formData) => {
    try {
      const response = await this._api.post("/admin", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateAdmin = async (formData, itemId) => {
    try {
      const response = await this._api.post("/admin/edit/" + itemId, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeAdmin = async (itemId) => {
    try {
      const response = await this._api.post("/admin/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleAdmins = async (data) => {
    try {
      const response = await this._api.post("/admin/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAdmin = async (itemId) => {
    try {
      const response = await this._api.get("/admin/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAdmins = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/admin/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region TOP HOSPITAL
  static addTopHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital-list", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateTopHospital = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/hospital-list/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeTopHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital-list/remove/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleTopHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital-list/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTopHospitals = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/hospital-list/top-hospital", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region OTHER HOSPITAL
  static addOtherHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital-list", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateOtherHospital = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/hospital-list/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeOtherHospital = async (itemId) => {
    try {
      const response = await this._api.post("/hospital-list/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleOtherHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital-list/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchOtherHospitals = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/hospital-list/other-hospital", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region AVAILIBILITY
  static addAvailibility = async (data) => {
    try {
      const response = await this._api.post("/availability", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateAvailibility = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/availability/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeAvailibility = async (itemId) => {
    try {
      const response = await this._api.post("/availability/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleAvailibilitys = async (data) => {
    try {
      const response = await this._api.post("/availability/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAvailibilitys = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/availability/list", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region SCHEDULE
  static fetchAvailableInterpreters = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/schedule/available-interpreters", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static addLunchSchedule = async (data) => {
    try {
      const response = await this._api.post("/schedule/lunch-store", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static addSchedule = async (data) => {
    try {
      const response = await this._api.post("/schedule", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateSchedule = async (data, itemId) => {
    try {
      const response = await this._api.post("/schedule/edit/" + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeSchedule = async (itemId) => {
    try {
      const response = await this._api.post("/schedule/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleSchedules = async (data) => {
    try {
      const response = await this._api.post("/schedule/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchSchedules = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/schedule-list", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchScheduleSummaryList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/schedule/summary", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchScheduleView = async (id, query) => {
    // let params = { page: page };
    // if (query && Object.keys(query).length > 0) {
    //   Object.keys(query).map((value) => {
    //     if (query[value]) {
    //       params[value] = query[value];
    //     }
    //   });
    // };

    try {
      const response = await this._api.get(`/schedule/view/${id}`, {
        // params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region PATIENT
  static addPatient = async (data) => {
    try {
      const response = await this._api.post("/patient", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updatePatient = async (data, itemId) => {
    try {
      const response = await this._api.post("/patient/edit/" + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removePatient = async (itemId) => {
    try {
      const response = await this._api.post("/patient/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultiplePatients = async (data) => {
    try {
      const response = await this._api.post("/patient/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchPatients = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/patient/list", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region CRM APIS

  //#region CLIENT

  static statusChange = async (itemId, data, path) => {
    const endpoint = `/client/${path}/status/`;
    try {
      const response = await this._api.post(`${endpoint}` + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region Client Templates
  static addTemplate = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/templates",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateTemplate = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/templates/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeTemplate = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/templates/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleTemplates = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/templates/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTemplates = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/subclients/templates/list",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTemplatesListPage = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/subclients/templates/list-page",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region TYPES
  static addType = async (data) => {
    try {
      const response = await this._api.post("/client/subclients/types", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateType = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeType = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleTypes = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTypes = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/subclients/types/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTypesListPage = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/subclients/types/list-page",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region ACCOUNTS
  static addAccount = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/accounts",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateAccount = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/accounts/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeAccount = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/accounts/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleAccounts = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/accounts/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAccounts = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/subclients/accounts/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchAccountsListPage = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/subclients/accounts/list-page",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainClients = async (mainclient_id, query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        `/client/subclients/types/${mainclient_id}/dynamic-fields`,
        {
          params,
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchSubClients = async (subclient_id, query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        `/client/subclients/accounts/${subclient_id}/dynamic-fields`,
        {
          params,
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region  MainAccount access Subclinet

  static fetchMainSubAccountsList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/subaccounts/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static addMainSubAccount = async (data) => {
    try {
      const response = await this._api.post("/account/subaccounts", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateMainSubAccount = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/account/subaccounts/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMainSubAccount = async (itemId) => {
    try {
      const response = await this._api.post(
        "/account/subaccounts/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleMainSubAccounts = async (data) => {
    try {
      const response = await this._api.post(
        "/account/subaccounts/records",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainAccountInvoices = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/invoices/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  // Main account profile page slice
  // static mainAccountChangePassword = async (data) => {
  //   try {
  //     const response = await this._api.post("/changepassword", data, {
  //       headers: {
  //         "Authorization": `Bearer ${Api._token}`
  //       }
  //     });
  //     return response;
  //   } catch (error) {
  //     return error.response;
  //   }
  // };

  static fetchMainAccountProfile = async () => {
    try {
      const response = await this._api.get("/account/profile", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateMainAccountProfile = async (formData) => {
    try {
      const response = await this._api.post(
        "/account/profile/update",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region STAFF
  static addStaff = async (data) => {
    try {
      const response = await this._api.post("/client/staff", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateStaff = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/staff/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeStaff = async (itemId) => {
    try {
      const response = await this._api.post("/client/staff/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleStaff = async (data) => {
    try {
      const response = await this._api.post("/client/staff/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchStaff = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/staff/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region DEPARTMENT
  static addDepartment = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/departments",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateDepartment = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/departments/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeDepartment = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/departments/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleDepartment = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/departments/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchDepartments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/subclients/types/departments/list",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region FACILITIES
  static addFacility = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/facilities",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateFacility = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/facilities/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeFacility = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/facilities/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleFacilities = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/facilities/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchFacilities = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/subclients/types/facilities/list",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region CLIENT Interpreter
  static addInterpreter = async (data) => {
    try {
      const response = await this._api.post("/client/interpreters", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateInterpreter = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/interpreters/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeInterpreter = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/interpreters/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleInterpreters = async (data) => {
    try {
      const response = await this._api.post(
        "/client/interpreters/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreters = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/interpreters/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region CLIENT Appointment
  static addAppointment = async (data) => {
    try {
      const response = await this._api.post("/client/appointments", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        timeout: 90000,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateAppointment = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/appointments/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeAppointment = async (itemId) => {
    console.log("Api._token", Api._token);
    try {
      const response = await this._api.post(
        `/client/appointments/delete/${itemId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleAppointments = async (data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAppointmentIds = async (page, query) => {
    let params = { page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).forEach((key) => {
        if (query[key] !== undefined && query[key] !== null && query[key] !== '') {
          params[key] = query[key];
        }
      });
    }
    try {
      const response = await this._api.get("/client/appointments/list-ids", {
        headers: { Authorization: `Bearer ${Api._token}` },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAppointments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/appointments/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static filterPatient = async (data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/filter-patient",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAppointClients = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/invoices/appt-clients", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAppointClientsListPage = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/invoices/appt-clients-list",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchGeneratedInvoices = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/invoices/generated-invoices",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static makeInvoice = async (data) => {
    try {
      const response = await this._api.post(
        "/client/invoices/make-invoice",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeInvoices = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/invoices/delete-invoices/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleInvoices = async (data) => {
    try {
      const response = await this._api.post(
        "/client/invoices/records-invoices",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region CLIENT DASHBOARD Appointment

  static fetchClientDashboardAppointment = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/dashboard/appointments", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static sendDashboardReminder = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/dashboard/send-reminder", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchClientDashboardAnalytics = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/dashboard/analytics", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  // #region permission group
  static fetchAccessesList = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/permissions/groups/accesses",
        {
          params,
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static addPermissionGroup = async (formData) => {
    try {
      const response = await this._api.post(
        "/client/permissions/groups",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updatePermissionGroup = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/permissions/groups/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removePermissionGroup = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/permissions/groups/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultiplePermissionGroup = async (data) => {
    try {
      const response = await this._api.post(
        "/client/permissions/groups/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchPermissionGroupList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/permissions/groups/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region subuser
  static addSubUsers = async (data) => {
    try {
      const response = await this._api.post(
        "/client/permissions/sub-users",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateSubUsers = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/permissions/sub-users/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeSubUsers = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/permissions/sub-users/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleSubUsers = async (data) => {
    try {
      const response = await this._api.post(
        "/client/permissions/sub-users/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchSubUsers = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/permissions/sub-users/list",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region Main Account DASHBOARD Appointment

  static fetchMainAccountDashboardAppointment = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/dashboard/appointments", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainAccountDashboardAnalytics = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/dashboard/analytics", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainAccountAppointments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/appointments/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainAccountAppointmentView = async (itemId) => {
    try {
      const response = await this._api.get(
        "/account/appointments/get/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainAccountAppointmentLogs = async (id, data, endpoint) => {
    try {
      const response = await this._api.get(
        `/account/appointments/get-logs/${id}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region CLIENT APPOINTMENT VIEW
  static fetchClientAppointmentView = async (itemId) => {
    try {
      const response = await this._api.get(
        "/client/appointments/get/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region APPOINTMENTS ACTIONS BUTTONS APIS
  static setActionAssign = async (id, data, endpoint) => {
    try {
      const response = await this._api.post(
        `/client/appointments/actions/${endpoint}/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          timeout: 90000,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region INTERPRETER APPOINTMENTS ACTIONS BUTTONS APIS
  static setInterpreterActionAssign = async (id, data, endpoint) => {
    try {
      const response = await this._api.post(
        `/interpreter/appointments/actions/${endpoint}/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          timeout: 90000,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region APPOINTMENTS ACTIONS BUTTONS APIS
  static setVendorActionAssign = async (id, data, endpoint) => {
    try {
      const response = await this._api.post(
        `/vendor/appointments/actions/${endpoint}/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          timeout: 90000,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region GET APPOINTMENTS LOGS
  static fetchAppointmentLogs = async (id, data, endpoint) => {
    try {
      const response = await this._api.get(
        `/client/appointments/get-logs/${id}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region CLIENT Appointments invite interpreters

  static fetchAppointmentsInterpreters = async (id) => {
    try {
      const response = await this._api.get(
        "/client/appointments/get-excluded-interpreters/" + id,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchRemainingInterpreters = async (id) => {
    try {
      const response = await this._api.get(
        "/client/appointments/get-remaining-interpreters/" + id,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpretersWithAppointments = async (id) => {
    try {
      const response = await this._api.get(
        "/client/appointments/get-interpreters-with-appointments/" + id,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInvitedInterpreters = async (id) => {
    try {
      const response = await this._api.get(
        "/client/appointments/get-invited-interpreters/" + id,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static inviteInterpreters = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/invite-interpreters/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeInvitedInterpreters = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/clear-invites/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region CLIENT Appointments INVOICES

  static fetchVerifiedInvoices = async (page, query) => {
    let params = { page: page, verified: 1 };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/invoices/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchUnVerifiedInvoices = async (page, query) => {
    let params = { page: page, verified: 0 };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/invoices/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateInvoice = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/invoices/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static verifyInvoice = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/invoices/verify/" + itemId,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region GET APPOINTMENTS invite
  static fetchInterviewInvites = async (token) => {
    try {
      const response = await this._api.get("/get-appointment/" + token, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static acceptInterviewInvites = async (token) => {
    try {
      const response = await this._api.post("/accept-appointment/" + token, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static rejectInterviewInvites = async (token) => {
    try {
      const response = await this._api.post("/reject-appointment/" + token, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region Payments
  static handleMultipleStatus = async (data) => {
    try {
      const response = await this._api.post("/client/payments/status/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAdminInterpreterPayments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/payments/list-interpreter",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchAdminVendorPayments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/payments/list-vendor", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region Interpreter access
  static fetchInterpreterDashboardAppointments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/interpreter/dashboard/appointments",
        {
          params,
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterDashboardAnalytics = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/dashboard/analytics", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterAppointments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/appointments/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterAssignedJobs = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/appointments/assigned-list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchInterpreterAvailableJobs = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/appointments/available-list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchInterpreterInvoices = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/interpreterInvoices/list-interpreter-invoices", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static downloadInterpreterInvoice = async (appointmentId) => {
    try {
      const response = await this._api.get(
        `/interpreter/interpreterInvoices/download-interpreter-invoice/${appointmentId}`,
        {
          responseType: "blob", // for PDF download
          headers: {
            Authorization: `Bearer ${Api._token}`, // send token like other APIs
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // static fetchInterpreterInvoices = async (page, query) => {
  //   let params = { page: page };
  //   if (query && Object.keys(query).length > 0) {
  //     Object.keys(query).map((value) => {
  //       if (query[value]) {
  //         params[value] = query[value];
  //       }
  //     });
  //   }
  //   try {
  //     const response = await this._api.get(
  //       "/client/payments/list-interpreter-invoices",
  //       {
  //         headers: {
  //           Authorization: `Bearer ${Api._token}`,
  //         },
  //         params,
  //       }
  //     );
  //     return response;
  //   } catch (error) {
  //     return error.response;
  //   }
  // };
  static fetchInterpreterCompletedJobs = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/appointments/completed-list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterAppointmentView = async (itemId) => {
    try {
      const response = await this._api.get(
        "/interpreter/appointments/get/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterAppointmentLogs = async (id, data, endpoint) => {
    try {
      const response = await this._api.get(
        `/interpreter/appointments/get-logs/${id}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static downloadTranslatefile = async (file) => {
    try {
      const response = await Api._api.post(
        `client/appointments/download-translate-file`,
        { file },
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          responseType: "blob", // file download ke liye zaroori
        }
      );

      return response;
    } catch (error) {
      return error.response;
    }
  };
  static downloadTranslation = async (file) => {
    try {
      const response = await Api._api.post(
        `interpreter/appointments/download-translation`,
        { file },
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          responseType: "blob", // file download ke liye zaroori
        }
      );

      return response;
    } catch (error) {
      return error.response;
    }
  };

  static uploadTranslatedFile = async (data) => {
    try {
      const response = await Api._api.post(
        `interpreter/appointments/upload-translation`,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterPayments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/payments/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterProfile = async () => {
    try {
      const response = await this._api.get("/interpreter/profile", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateInterpreterProfile = async (formData) => {
    try {
      const response = await this._api.post(
        "/interpreter/profile/update",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // Self-service "dates I am not available for work"
  static fetchMyUnavailableDates = async () => {
    try {
      const response = await this._api.get("/interpreter/profile/availability", {
        headers: { Authorization: `Bearer ${Api._token}` },
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };

  static addMyUnavailableDate = async (startDate, endDate) => {
    try {
      const response = await this._api.post(
        "/interpreter/profile/availability",
        { start_date: startDate, end_date: endDate },
        { headers: { Authorization: `Bearer ${Api._token}` } }
      );
      return response;
    } catch (error) {
      return error?.response;
    }
  };

  static deleteMyUnavailableDate = async (id) => {
    try {
      const response = await this._api.post(
        `/interpreter/profile/availability/delete/${id}`,
        {},
        { headers: { Authorization: `Bearer ${Api._token}` } }
      );
      return response;
    } catch (error) {
      return error?.response;
    }
  };
  //#endregion

  //#region Vendor access
  static fetchVendorDashboardAppointments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/vendor/dashboard/appointments", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchVendorDashboardAnalytics = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/vendor/dashboard/analytics", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchVendorAppointments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/vendor/appointments/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchVendorAppointmentView = async (itemId) => {
    try {
      const response = await this._api.get(
        "/vendor/appointments/get/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchVendorAppointmentLogs = async (id, data, endpoint) => {
    try {
      const response = await this._api.get(
        `/vendor/appointments/get-logs/${id}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchVendorPayments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/vendor/payments/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchVendorProfile = async () => {
    try {
      const response = await this._api.get("/vendor/profile", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateVendorProfile = async (formData) => {
    try {
      const response = await this._api.post(
        "/vendor/profile/update",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region vendor Interpreter
  static addVendorInterpreter = async (data) => {
    try {
      const response = await this._api.post("/vendor/interpreters", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateVendorInterpreter = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/vendor/interpreters/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeVendorInterpreter = async (itemId) => {
    try {
      const response = await this._api.post(
        "/vendor/interpreters/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleVendorInterpreters = async (data) => {
    try {
      const response = await this._api.post(
        "/vendor/interpreters/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchVendorInterpreters = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/vendor/interpreters/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchAvailableVendorInterpreters = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get(
        "/vendor/interpreters/available-interpreters",
        {
          params,
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static vendorInterpreterstatusChange = async (itemId, data, path) => {
    const endpoint = `/${path}/status/`;
    try {
      const response = await this._api.post(`${endpoint}` + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static declineAppointments = async (formData) => {
    try {
      const response = await this._api.post(
        "/client/appointments/actions/decline",
        formData,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region admin Join Video Call
  static adminjoinVideoCall = async (appointmentId) => {
    try {
      const response = await Api._api.post(
        `/client/appointments/actions/join-room/${appointmentId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region interpriters Join Video Call
  static interpreterjoinVideoCall = async (appointmentId) => {
    try {
      const response = await Api._api.post(
        `/interpreter/appointments/actions/join-room/${appointmentId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static interpreterCheckinCheckout = async (data, itemId) => {
    try {
      const response = await this._api.post(
        `/interpreter/appointments/actions/adjustment/${itemId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region appoinment details
  static getAppointmentDetail = async (token) => {
    try {
      const response = await Api._api.get(
        `/get-appointment-requester/${token}`,
        {}
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static requesterJoinRoom = async (token) => {
    try {
      const response = await Api._api.post(`/join-room/${token}`, {});
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#endregion

  //#region admin translation form
  static submitTranslation = async (formData) => {
    try {
      const response = await this._api.post("/client/translations", formData, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // static fetchAppxointments = async (page, query) => {
  //     let params = { page: page };
  //     if (query && Object.keys(query).length > 0) {
  //       Object.keys(query).map((value) => {
  //         if (query[value]) {
  //           params[value] = query[value];
  //         }
  //       });
  //     }
  //     try {
  //       const response = await this._api.get("/client/appointments/list", {
  //         headers: {
  //           Authorization: `Bearer ${Api._token}`,
  //         },
  //         params,
  //       });
  //       return response;
  //     } catch (error) {
  //       return error.response;
  //     }
  //   };

  //#region admin translation
  static fetchTranslation = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/translations/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTranslationById = async (transId) => {
    try {
      const response = await this._api.get(
        `/client/translations/get/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTransLogs = async (transId) => {
    try {
      const response = await this._api.get(
        `/client/translations/get-logs/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static approveQuote = async (transId) => {
    try {
      const response = await this._api.post(
        `/client/quotations/approve/${transId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static rejectQuote = async (transId, payload) => {
    try {
      const response = await this._api.post(
        `/client/quotations/reject/${transId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // Revise Quote
  static fetchReviseQuote = async (transId) => {
    try {
      const response = await this._api.get(
        `/client/quotations/latest/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static getTranslators = async (transId) => {
    try {
      const response = await this._api.get(
        `/client/translations/get-translators/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static getInvitedTranslators = async (transId) => {
    try {
      const response = await this._api.get(
        `/client/translations/get-invited-translators/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static inviteTranslators = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/translations/invite-translators/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static assignTranslators = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/assign-translator/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static translationApproveSubmission = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/approve-submission/" + itemId,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static translationRejectSubmission = async (itemId, payload) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/reject-submission/" + itemId,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static translationMarkInvoiceAsPaid = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/change-invoice-status/" + itemId,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static translationCancel = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/cancel/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static translationUploadDocument = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/upload-translated-files/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  // Update Revise Quotes
  static updateReviseQuote = async (transId, payload) => {
    try {
      const response = await this._api.post(
        `/client/quotations/update/${transId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region client translation
  static fetchclientTranslationList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/translations/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchclientTranslationById = async (transId) => {
    try {
      const response = await this._api.get(
        `/account/translations/get/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // client code
  static approveClientQuote = async (transId) => {
    try {
      const response = await this._api.post(
        `/account/quotations/approve/${transId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // client reject quote
  static rejectClientQuote = async (transId, payload) => {
    try {
      const response = await this._api.post(
        `/account/quotations/reject/${transId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchclientTransLogs = async (transId) => {
    try {
      const response = await this._api.get(
        `/account/translations/get-logs/${transId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region client translation form
  static submitClientTranslationForm = async (formData) => {
    try {
      const response = await this._api.post("/account/translations", formData, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // Client Cancel Translation
  static clientCancelTranslation = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/account/translations/actions/cancel/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region Interpreter translation
  static fetchInterpreterTranslationList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/translations/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region Interpreter translation By Id
  static fetchinterpreterTranslationById = async (transId) => {
    try {
      const response = await this._api.get(
        `/interpreter/translations/get/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // Interpreter Upload Translation Document
  static interpreterTranslationUploadDocument = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/interpreter/translations/actions/upload-translated-files/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // Interpreter Decline Translation
  static interpreterDeclineTranslation = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/interpreter/translations/actions/decline-translation/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion Answer appointment invites
  static answerAppointmentInvite = async (apptId, payload) => {
    try {
      const response = await this._api.post(
        `/interpreter/appointments/actions/answer-invite/${apptId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region Decline translation
  static declineTranslationById = async (transId, payload) => {
    try {
      const response = await this._api.post(
        `/interpreter/translations/actions/decline-invite/${transId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region cancel Job 
  static cancelJob = async (apptId, payload) => {
    try {
      const response = await this._api.post(
        `/interpreter/appointments/actions/cancel-job/${apptId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region Accept translation
  static acceptTranslationById = async (transId) => {
    try {
      const response = await this._api.post(
        `/interpreter/translations/actions/accept-invite/${transId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region VRI CALLING
  static acceptVriCall = async (id) => {
    try {
      const response = await this._api.post(
        `/interpreter/appointments/actions/answer-vri-call/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region Fetch Client
  static fetchClient = async () => {
    try {
      const response = await this._api.get("/client/subclients/types/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region Fetch Translator
  static fetchTranslator = async () => {
    try {
      const response = await this._api.get(
        "/client/translations/get-all-translators",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static inviteGuests = async (appointmentId, payload) => {
    try {
      const response = await this._api.post(
        `/client/appointments/actions/invite-guests/${appointmentId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };


  static addHolidayRate = async (data) => {
    try {
      const response = await this._api.post("/client/holiday-rates", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateHolidayRate = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/holiday-rates/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchHolidayRates = async (page, query) => {
    let params = { page: page };

    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/client/holiday-rates", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // services/api.js

  static async removeMultipleHolidayRates(payload) {
    console.log('[API DEBUG] removeMultipleHolidayRates called with:', payload);
    try {
      const response = await this._api.post(
        "/client/holiday-rates/delete-multiple",
        payload,
        {
          headers: { Authorization: `Bearer ${Api._token}` },
        }
      );
      console.log("[API DEBUG] response:", response);
      return response;
    } catch (error) {
      console.error("[API DEBUG] error:", error);
      return error?.response;
    }
  }

  static addMiscRate = async (data) => {
    try {
      const response = await this._api.post("/client/misc-rates", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateMiscRate = async (data, itemId) => {
    try {
      const response = await this._api.post(
        `/client/misc-rates/edit/${itemId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMiscRates = async (page, query) => {
    let params = { page: page };

    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((key) => {
        if (query[key]) {
          params[key] = query[key];
        }
      });
    }

    try {
      const response = await this._api.get("/client/misc-rates", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };


  static removeMultipleMiscRates = async (payload) => {
    try {
      const response = await this._api.post(
        "/client/misc-rates/delete-multiple",
        payload,
        {
          headers: { Authorization: `Bearer ${Api._token}` },
        }
      );
      console.log("[API DEBUG] response:", response);
      return response;
    } catch (error) {
      console.error("[API DEBUG] error:", error);
      return error?.response;
    }
  };

  static addPoeta = async (data) => {
    try {
      const response = await this._api.post("/client/poeta", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchPoetas = async (page, query) => {
    let params = { page: page };

    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((key) => {
        if (query[key]) {
          params[key] = query[key];
        }
      });
    }
    try {
      const response = await this._api.get("/client/poeta", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchCourtTypes = async (page, query) => {
    let params = { page: page };

    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((key) => {
        if (query[key]) {
          params[key] = query[key];
        }
      });
    }
    try {
      const response = await this._api.get("/client/court-types/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updatePoeta = async (data, itemId) => {
    try {
      const response = await this._api.post(
        `/client/poeta/edit/${itemId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultiplePoetas = async (payload) => {
    try {
      const response = await this._api.post(
        "/client/poeta/delete-multiple",
        payload,
        {
          headers: { Authorization: `Bearer ${Api._token}` },
        }
      );
      console.log("[API DEBUG] response:", response);
      return response;
    } catch (error) {
      console.error("[API DEBUG] error:", error);
      return error?.response;
    }
  };

  static fetchInterpreterById = async (id) => {
    try {
      const response = await this._api.get(`/client/interpreters/fetchSingleRecord/${id}`, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };




  static downloadFullPaymentReport = async (query) => {
    try {
      let params = { page: 1 };
      if (query && Object.keys(query).length > 0) {
        Object.keys(query).map((value) => {
          if (query[value]) {
            params[value] = query[value];
          }
        });
      }
      const response = await this._api.get(
        "/client/payments/download-full-report",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params
        }
      );
      return response;
    } catch (error) {
      console.error("[API DEBUG] error:", error);
      return error?.response;
    }
  };
  static fetchApReports = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/ap_reports/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };


  //#region API LOGS (KCDC outbound events)
  static fetchApiLogs = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/api-logs/list", {
        headers: { Authorization: `Bearer ${Api._token}` },
        params,
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };

  static fetchApiLogDetail = async (id) => {
    try {
      const response = await this._api.get(`/client/api-logs/view/${id}`, {
        headers: { Authorization: `Bearer ${Api._token}` },
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };
  //#endregion

  //#region NOTIFICATION LOGS (outgoing emails)
  static fetchNotificationLogs = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/notification-logs/list", {
        headers: { Authorization: `Bearer ${Api._token}` },
        params,
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };

  static fetchNotificationLogDetail = async (id) => {
    try {
      const response = await this._api.get(`/client/notification-logs/view/${id}`, {
        headers: { Authorization: `Bearer ${Api._token}` },
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };

  // Interpreter portal — server-side scoped to the logged-in interpreter's own emails
  static fetchMyNotificationLogs = async (appointmentId) => {
    try {
      const response = await this._api.get(`/interpreter/appointments/notification-logs/${appointmentId}`, {
        headers: { Authorization: `Bearer ${Api._token}` },
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };

  static fetchMyNotificationLogDetail = async (id) => {
    try {
      const response = await this._api.get(`/interpreter/appointments/notification-logs/view/${id}`, {
        headers: { Authorization: `Bearer ${Api._token}` },
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };
  //#endregion

  //#region ADMIN REPORTS (Non Invoiced Jobs / Raw Data)
  static fetchNonInvoicedJobsReport = async (params) => {
    try {
      const response = await this._api.get("/client/reports/non-invoiced-jobs", {
        headers: { Authorization: `Bearer ${Api._token}` },
        params,
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };

  static downloadNonInvoicedJobsReport = async (params) => {
    try {
      const response = await this._api.get("/client/reports/non-invoiced-jobs/download", {
        headers: { Authorization: `Bearer ${Api._token}` },
        params,
        responseType: "blob",
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };

  static fetchRawDataReport = async (params) => {
    try {
      const response = await this._api.get("/client/reports/raw-data", {
        headers: { Authorization: `Bearer ${Api._token}` },
        params,
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };

  static downloadRawDataReport = async (params) => {
    try {
      const response = await this._api.get("/client/reports/raw-data/download", {
        headers: { Authorization: `Bearer ${Api._token}` },
        params,
        responseType: "blob",
      });
      return response;
    } catch (error) {
      return error?.response;
    }
  };
  //#endregion

  static downloadConsolidatedInvoice = async (reportId) => {
    try {
      const response = await this._api.get(
        `/client/ap_reports/${reportId}/download`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          responseType: "blob",
        }
      );
      return response;
    } catch (error) {
      console.error("[API DEBUG] downloadConsolidatedInvoice error:", error);
      return error?.response;
    }
  };



  // Invite Guests 


}